/**
 * The bot character library: every character DreamBot draws, as plain data.
 *
 * Nothing here touches the DOM, so the web renderer (`BotCharacter`) and any future native
 * renderer read the same outlines and eye positions. Append new characters at the end: a
 * bot's saved look stores a character by its position in this list.
 */
export type CharacterId =
  | "flower"
  | "mushroom"
  | "drop"
  | "heart"
  | "cloud"
  | "tile"
  | "ghost"
  | "bubble"
  | "cat"
  | "bear"
  | "sprout"
  | "bunny";

export type MouthId = "smile" | "grin" | "open" | "cat";

export type Character = {
  id: CharacterId;
  /**
   * Body outline in a 100 x 100 box. Sub-paths all wind clockwise, so the ears and leaves of a
   * character merge into one silhouette under the non-zero fill rule.
   */
  path: string;
  /** Eye height, and the distance of each eye from the centre line. */
  eyeY: number;
  eyeGap: number;
  mouth: MouthId;
  /** Height of the mouth's centre. */
  mouthY: number;
  /** Lighter patches on the body (spots, a muzzle, inner ears) as `[x, y, rx, ry]`. */
  marks?: readonly (readonly [number, number, number, number])[];
};

/** A soft flower: a circle whose radius swells and dips `lobes` times. */
function lobedPath(lobes: number, base: number, amp: number): string {
  const steps = 96;
  const points = Array.from({ length: steps }, (_, index) => {
    const turn = (index / steps) * Math.PI * 2;
    const radius = base * (1 + amp * Math.cos(lobes * turn));
    const x = 50 + radius * Math.sin(turn);
    const y = 50 - radius * Math.cos(turn);
    return `${x.toFixed(2)} ${y.toFixed(2)}`;
  });
  return `M${points.join("L")}Z`;
}

const KAPPA = 0.5523;

/** A clockwise ellipse from four cubic curves; the renderers share no arc command. */
function ellipsePath(cx: number, cy: number, rx: number, ry: number): string {
  const kx = KAPPA * rx;
  const ky = KAPPA * ry;
  return (
    `M${cx} ${cy - ry}C${cx + kx} ${cy - ry} ${cx + rx} ${cy - ky} ${cx + rx} ${cy}` +
    `C${cx + rx} ${cy + ky} ${cx + kx} ${cy + ry} ${cx} ${cy + ry}` +
    `C${cx - kx} ${cy + ry} ${cx - rx} ${cy + ky} ${cx - rx} ${cy}` +
    `C${cx - rx} ${cy - ky} ${cx - kx} ${cy - ry} ${cx} ${cy - ry}Z`
  );
}

/** A clockwise rounded rectangle. */
function roundRectPath(
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number,
): string {
  const r = radius;
  const k = KAPPA * r;
  const right = x + width;
  const bottom = y + height;
  return (
    `M${x + r} ${y}H${right - r}C${right - r + k} ${y} ${right} ${y + r - k} ${right} ${y + r}` +
    `V${bottom - r}C${right} ${bottom - r + k} ${right - r + k} ${bottom} ${right - r} ${bottom}` +
    `H${x + r}C${x + r - k} ${bottom} ${x} ${bottom - r + k} ${x} ${bottom - r}` +
    `V${y + r}C${x} ${y + r - k} ${x + r - k} ${y} ${x + r} ${y}Z`
  );
}

export const BOT_CHARACTERS: readonly Character[] = [
  {
    id: "flower",
    path: lobedPath(6, 40, 0.09),
    eyeY: 48,
    eyeGap: 16,
    mouth: "smile",
    mouthY: 66,
  },
  {
    id: "mushroom",
    path: "M50 8C26 8 8 26 8 46c0 6 4 10 10 10h12v22c0 8 6 14 14 14h12c8 0 14-6 14-14V56h12c6 0 10-4 10-10C92 26 74 8 50 8Z",
    eyeY: 38,
    eyeGap: 17,
    mouth: "grin",
    mouthY: 66,
    marks: [
      [50, 16, 7, 4.6],
      [17, 31, 4, 3.4],
      [83, 31, 4, 3.4],
    ],
  },
  {
    id: "drop",
    path: "M50 6S14 44 14 64c0 18 16 30 36 30s36-12 36-30C86 44 50 6 50 6Z",
    eyeY: 60,
    eyeGap: 14,
    mouth: "open",
    mouthY: 76,
  },
  {
    id: "heart",
    path: "M50 90S8 62 8 34C8 20 19 10 32 10c9 0 15 5 18 11 3-6 9-11 18-11 13 0 24 10 24 24 0 28-42 56-42 56Z",
    eyeY: 40,
    eyeGap: 15,
    mouth: "smile",
    mouthY: 57,
  },
  {
    id: "cloud",
    path: [
      roundRectPath(8, 52, 84, 34, 17),
      ellipsePath(33, 49, 21, 21),
      ellipsePath(60, 40, 25, 25),
    ].join(""),
    eyeY: 60,
    eyeGap: 17,
    mouth: "cat",
    mouthY: 75,
  },
  {
    id: "tile",
    path: "M30 10h40c14 0 22 8 22 22v36c0 14-8 22-22 22H30C16 90 8 82 8 68V32C8 18 16 10 30 10Z",
    eyeY: 46,
    eyeGap: 17,
    mouth: "smile",
    mouthY: 66,
  },
  {
    id: "ghost",
    path: "M10 86V48C10 26 28 8 50 8s40 18 40 40v38C90 96 63.3 96 63.3 86C63.3 96 36.7 96 36.7 86C36.7 96 10 96 10 86Z",
    eyeY: 44,
    eyeGap: 16,
    mouth: "open",
    mouthY: 62,
  },
  {
    id: "bubble",
    path: "M50 10c24 0 42 16 42 38 0 22-18 38-40 38-4 0-8 0-12-1L22 94l4-16C14 71 8 60 8 48 8 26 26 10 50 10Z",
    eyeY: 44,
    eyeGap: 16,
    mouth: "smile",
    mouthY: 62,
  },
  {
    id: "cat",
    path: [
      ellipsePath(50, 60, 40, 33),
      "M11 50C9 34 11 18 16 10C18 7 22 8 26 11C32 15 38 21 44 29Z",
      "M56 29C62 21 68 15 74 11C78 8 82 7 84 10C89 18 91 34 89 50Z",
    ].join(""),
    eyeY: 56,
    eyeGap: 18,
    mouth: "cat",
    mouthY: 72,
    marks: [
      [20, 24, 2.6, 6],
      [80, 24, 2.6, 6],
    ],
  },
  {
    id: "bear",
    path: [
      ellipsePath(50, 56, 38, 36),
      ellipsePath(21, 24, 13, 13),
      ellipsePath(79, 24, 13, 13),
    ].join(""),
    eyeY: 50,
    eyeGap: 17,
    mouth: "smile",
    mouthY: 71,
    marks: [
      [21, 24, 6.4, 6.4],
      [79, 24, 6.4, 6.4],
      [50, 68, 15, 11],
    ],
  },
  {
    id: "sprout",
    path: [
      ellipsePath(50, 62, 36, 31),
      "M50 33C32 36 20 26 20 12C34 10 46 18 50 33Z",
      "M50 33C54 18 66 10 80 12C80 26 68 36 50 33Z",
    ].join(""),
    eyeY: 60,
    eyeGap: 16,
    mouth: "smile",
    mouthY: 76,
  },
  {
    id: "bunny",
    path: [
      ellipsePath(50, 64, 36, 29),
      ellipsePath(34, 25, 9, 20),
      ellipsePath(66, 25, 9, 20),
    ].join(""),
    eyeY: 62,
    eyeGap: 17,
    mouth: "cat",
    mouthY: 77,
    marks: [
      [34, 26, 4.2, 13],
      [66, 26, 4.2, 13],
    ],
  },
];

/** Mouths, drawn around the origin: moved to `(50, mouthY)`, stroked or filled in the mouth ink. */
export const MOUTHS: Record<MouthId, { d: string; filled: boolean }> = {
  smile: { d: "M-5.5 0Q0 5.5 5.5 0", filled: false },
  grin: { d: "M-6.5 -1H6.5C6.5 4 3.6 7.5 0 7.5C-3.6 7.5 -6.5 4 -6.5 -1Z", filled: true },
  open: {
    d: "M0 -3C2.4 -3 3.2 -1 3.2 1.4C3.2 3.6 1.8 4.6 0 4.6C-1.8 4.6 -3.2 3.6 -3.2 1.4C-3.2 -1 -2.4 -3 0 -3Z",
    filled: true,
  },
  cat: { d: "M-6 -0.5Q-3 4.6 0 0.4Q3 4.6 6 -0.5", filled: false },
};

/**
 * How every character is lit and faced, in the 100 x 100 box, so the web and native renderers
 * paint the same bot: a radial body shade, a lighter rim along the top-left edge, a floor shade,
 * a gloss, blushing cheeks and glossy eyes.
 */
export const CHARACTER_LOOK = {
  /** Paint factors against the body colour, as in the DreamBot logo. */
  light: 1.16,
  shadow: 0.74,
  deep: 0.42,
  /** How far the rim tint is mixed toward white. */
  rimMix: 0.62,
  body: { cx: 58, cy: 30, r: 82 },
  /** The body is drawn this far down-right of the silhouette; the gap on the top-left is the rim. */
  rimShift: { x: 1.3, y: 1.9 },
  floor: { cx: 50, cy: 100, rx: 56, ry: 38, opacity: 0.4 },
  gloss: { cx: 34, cy: 23, rx: 15, ry: 7.5, angle: -28, opacity: 0.6 },
  eye: { rx: 8, ry: 9.6, pupil: 5, ink: "#17171a" },
  glint: { x: 1.9, y: -2.1, r: 1.9 },
  spark: { x: -1.8, y: 2.1, r: 0.9, opacity: 0.7 },
  cheek: { dx: 9, dy: 11, rx: 5.2, ry: 3.2, color: "#ff8fa8", opacity: 0.55 },
  mark: { color: "#ffffff", opacity: 0.3 },
  mouthWidth: 2.4,
} as const;

function expandHex(hex: string): string | undefined {
  const match = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.exec(hex);
  const digits = match?.[1];
  if (!digits) return undefined;
  return digits.length === 3 ? [...digits].map((digit) => digit + digit).join("") : digits;
}

function channels(hex: string): [number, number, number] | undefined {
  const full = expandHex(hex);
  if (!full) return undefined;
  return [0, 2, 4].map((at) => Number.parseInt(full.slice(at, at + 2), 16)) as [
    number,
    number,
    number,
  ];
}

function toHex(values: readonly number[]): string {
  return `#${values
    .map((value) =>
      Math.max(0, Math.min(255, Math.round(value)))
        .toString(16)
        .padStart(2, "0"),
    )
    .join("")}`;
}

/** The body colour's tones. An unreadable colour is used as-is for every tone. */
export function characterPaint(hex: string) {
  const rgb = channels(hex);
  if (!rgb) return { base: hex, light: hex, shadow: hex, deep: hex, rim: hex };
  const scale = (factor: number) => toHex(rgb.map((value) => value * factor));
  const { light, shadow, deep, rimMix } = CHARACTER_LOOK;
  return {
    base: scale(1),
    light: scale(light),
    shadow: scale(shadow),
    deep: scale(deep),
    rim: toHex(rgb.map((value) => value + (255 - value) * rimMix)),
  };
}

export const BOT_CHARACTER_COUNT = BOT_CHARACTERS.length;

const FIRST_CHARACTER: Character = BOT_CHARACTERS[0]!;

function wrapIndex(index: number): number {
  const count = BOT_CHARACTER_COUNT;
  return ((Math.trunc(index) % count) + count) % count;
}

export function characterAt(index: number): Character {
  return BOT_CHARACTERS[wrapIndex(index)] ?? FIRST_CHARACTER;
}

export function characterById(id: CharacterId): Character {
  return BOT_CHARACTERS.find((character) => character.id === id) ?? FIRST_CHARACTER;
}

export function characterIndexOfId(id: CharacterId): number {
  return Math.max(
    0,
    BOT_CHARACTERS.findIndex((character) => character.id === id),
  );
}

/**
 * A bot's look is stored as `#RRGGBB::shape_N`, the same value the shipped shapes use, so every
 * client that predates this library still reads a colour and a shape. `N` is the character's
 * position in `BOT_CHARACTERS`.
 */
const STORED_LOOK = /^(#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6}))::shape_(\d+)$/;

/** The character a stored look names, or undefined for a plain colour. */
export function characterIndexOf(value: string): number | undefined {
  const match = STORED_LOOK.exec(value);
  const position = match?.[2];
  if (position === undefined) return undefined;
  const index = Number(position);
  return Number.isSafeInteger(index) ? index % BOT_CHARACTER_COUNT : undefined;
}

/** `value` with its character set to `index`, keeping its colour. */
export function withCharacter(value: string, index: number): string {
  const color = value.split("::shape_")[0] || "#F97316";
  return `${color}::shape_${wrapIndex(index)}`;
}

function hashString(text: string): number {
  let hash = 2166136261;
  for (let index = 0; index < text.length; index++) {
    hash ^= text.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
}

/**
 * The character to draw: the one the stored look names, else one picked from `identity`
 * (a bot id) so a bot saved without a choice still looks the same on every device.
 */
export function resolveCharacter(value: string, identity = ""): Character {
  const stored = characterIndexOf(value);
  if (stored !== undefined) return characterAt(stored);
  return characterAt(hashString(identity || value));
}

/** The least-used character, walking on from the newest one so repeats still rotate. */
export function nextCharacter(used: readonly CharacterId[]): CharacterId {
  const newest = used[0];
  const start = newest ? characterIndexOfId(newest) + 1 : 0;
  const usesOf = (id: CharacterId) => used.filter((candidate) => candidate === id).length;
  let best = characterAt(start).id;
  for (let step = 1; step < BOT_CHARACTER_COUNT; step++) {
    const candidate = characterAt(start + step).id;
    if (usesOf(candidate) < usesOf(best)) best = candidate;
  }
  return best;
}
